import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommunityGameNightCreate,
  CommunityGameNightRsvpRequest,
  CommunityGameNightUpdate,
  CommunityNotificationPreferenceUpdate,
  CommunityPollConvertRequest,
  CommunityPollCreate,
  CommunityPollVoteRequest,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

export function useCommunityGameNights(
  communityId: string,
  includePast = false,
  { coordination = true }: { coordination?: boolean } = {},
) {
  const client = useQueryClient();
  const { user, loading } = useAuth();
  const enabled = Boolean(communityId && user?.uid) && !loading;
  // The compact "Next event" card shows neither polls nor reminder settings.
  const coordinationEnabled = enabled && coordination;
  const key = [
    ...queryKeys.social.communityGameNights(communityId, includePast),
    user?.uid ?? null,
  ];
  const query = useQuery({
    queryKey: key,
    queryFn: () => communityApi.listGameNights(communityId, includePast),
    enabled,
    refetchOnMount: "always",
    staleTime: 0,
    retry: false,
  });

  const pollsKey = [
    ...queryKeys.social.communityPolls(communityId),
    user?.uid ?? null,
  ];
  const pollsQuery = useQuery({
    queryKey: pollsKey,
    queryFn: () =>
      communityApi.listPolls
        ? communityApi.listPolls(communityId)
        : Promise.resolve({ items: [], can_create: false }),
    enabled: coordinationEnabled,
    refetchOnMount: "always",
    staleTime: 0,
    retry: false,
  });

  const preferencesKey = [
    ...queryKeys.social.communityNotificationPreferences(communityId),
    user?.uid ?? null,
  ];
  const preferencesQuery = useQuery({
    queryKey: preferencesKey,
    queryFn: () =>
      communityApi.getNotificationPreferences
        ? communityApi.getNotificationPreferences(communityId)
        : Promise.resolve(null),
    enabled: coordinationEnabled,
    refetchOnMount: "always",
    staleTime: 0,
    retry: false,
  });

  const refresh = async () => {
    await client.invalidateQueries({
      queryKey: queryKeys.social.communityGameNights(communityId, false),
    });
    await client.invalidateQueries({
      queryKey: queryKeys.social.communityGameNights(communityId, true),
    });
    await client.invalidateQueries({
      queryKey: queryKeys.social.communityChannels(communityId),
    });
  };
  const refreshPolls = async () => {
    await client.invalidateQueries({
      queryKey: queryKeys.social.communityPolls(communityId),
    });
  };
  const refreshPreferences = async () => {
    await client.invalidateQueries({
      queryKey: queryKeys.social.communityNotificationPreferences(communityId),
    });
  };
  const create = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (data: CommunityGameNightCreate) =>
      communityApi.createGameNight(communityId, data),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: CommunityGameNightUpdate;
    }) => communityApi.updateGameNight(eventId, data),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: (eventId: string) => communityApi.cancelGameNight(eventId),
    onSuccess: refresh,
  });
  const rsvp = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: CommunityGameNightRsvpRequest;
    }) => communityApi.setGameNightRsvp(eventId, data),
    onSuccess: refresh,
  });
  const remind = useMutation({
    mutationFn: (eventId: string) =>
      communityApi.sendGameNightReminder(eventId),
    onSuccess: refresh,
  });
  const createPoll = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (data: CommunityPollCreate) =>
      communityApi.createPoll(communityId, data),
    onSuccess: refreshPolls,
  });
  const votePoll = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: ({
      pollId,
      data,
    }: {
      pollId: string;
      data: CommunityPollVoteRequest;
    }) => communityApi.votePoll(pollId, data),
    onSuccess: refreshPolls,
  });
  const convertPoll = useMutation({
    mutationFn: ({
      pollId,
      data,
    }: {
      pollId: string;
      data: CommunityPollConvertRequest;
    }) => communityApi.convertPollToGameNight(pollId, data),
    onSuccess: async () => {
      await refreshPolls();
      await refresh();
    },
  });
  const updatePreferences = useMutation({
    mutationFn: (data: CommunityNotificationPreferenceUpdate) =>
      communityApi.updateNotificationPreferences(communityId, data),
    onSuccess: refreshPreferences,
  });

  return {
    events: {
      ...query,
      data:
        enabled && query.isFetchedAfterMount && !query.isError
          ? query.data
          : undefined,
    },
    polls: {
      ...pollsQuery,
      data:
        coordinationEnabled && pollsQuery.isFetchedAfterMount && !pollsQuery.isError
          ? pollsQuery.data
          : undefined,
    },
    preferences: {
      ...preferencesQuery,
      data:
        coordinationEnabled &&
        preferencesQuery.isFetchedAfterMount &&
        !preferencesQuery.isError
          ? preferencesQuery.data
          : undefined,
    },
    create,
    update,
    cancel,
    rsvp,
    remind,
    createPoll,
    votePoll,
    convertPoll,
    updatePreferences,
  };
}

